/**
 * CouponRedemptionComponent 促核销主组件
 *
 * 促核销组件的入口组件，负责整体布局和数据获取
 * 集成 Header 和 List，支持配置标题、背景图片、上边距
 *
 * Requirements:
 * - 1.3: 仅在会员主页展示
 * - 8.1: 支持配置组件标题，最多6个字，默认为"权益·我的会员产品"
 * - 8.2: 支持配置组件背景图片 (bgUrl)
 * - 8.3: 支持配置组件上边距 (spacing)
 * - 8.4: 支持配置曝光人群
 */

import { useConfigPage } from '@/hooks/useConfigPage/useConfigPage';
import { goToPageV2, myInit, wxInit } from '@/utils/common';
import { trackGdp } from '@/utils/gio-report';
import { getBrowserEnv } from '@/utils/tools';
import { processOssImage } from '@xp-design/mobile/lib/utils/oss-process-image';
import React, { useCallback, useEffect, useMemo } from 'react';
import IconRightPng from '../../assets/icon_right.png';
import { useCouponRedemption } from '../../hooks/useCouponRedemption';
import { useUserContext } from '../../routes/home/userContext';
import {
  CouponRedemptionComponentProps,
  DEFAULT_COMPONENT_TITLE,
  DisplayItem,
  MyMemberGoodsResp,
  UnifiedCardItem,
} from '../../types/coupon-redemption';
import { getReportCompData, showToast } from '../../utils';
import CouponRedemptionList from '../coupon-redemption-list';
import './index.less';

/** 组件内容配置（来自B端配置） */
interface CompContent {
  /** 组件标题 */
  title?: string;
  /** 背景图片URL */
  bgUrl?: string;
  /** 上边距 */
  spacing?: number;
  /** 是否展示 */
  show?: boolean;
}

const { isMyApp, isWXApp, isXPApp } = getBrowserEnv();

/**
 * 促核销主组件
 *
 * @param props 组件属性
 */
const CouponRedemptionComponent: React.FC<CouponRedemptionComponentProps> = ({
  comp,
  config,
  audienceId,
}) => {
  // 从 context 获取数据
  const { myMemberGoods, userType, isLoading } = useUserContext();
  const { configPageQuery } = useConfigPage();

  // 解析 comp 中的配置内容
  const compContent = useMemo<CompContent>(() => {
    return comp?.moduleJsonData?.content || {};
  }, [comp]);

  // 获取背景图片和上边距配置
  const bgUrl = compContent.bgUrl || '';
  const spacing = compContent.spacing || 0;

  // 计算券类型用于埋点
  const couponType = useMemo(() => {
    const { userCouponList, userChargeCardList } = myMemberGoods || {};
    if (userCouponList?.length && userChargeCardList?.length) {
      return '充电券&储值卡';
    }
    if (userCouponList?.length) {
      return '充电券';
    }
    return '储值卡';
  }, [myMemberGoods]);

  // 获取数据 - 将 context 数据转换为 hook 需要的格式
  // TODO: 实现曝光人群判断逻辑
  // 当前硬编码为 true，需要根据 comp.moduleJsonData.crowd 配置判断用户是否在曝光人群中
  // 参考实现：
  // 1. 从 comp.moduleJsonData.crowd.dmpList 获取人群配置
  // 2. 调用后端接口或使用缓存的用户人群标签判断是否匹配
  // 3. 如果 audienceId 为空或未配置人群，则默认为 true（全部展示）
  const isInAudience = useMemo(() => {
    // 如果没有配置曝光人群，则全部展示
    if (!audienceId) return true;
    
    // TODO: 实现实际的人群判断逻辑
    // 可能需要：
    // - 从 UserContext 获取用户的人群标签
    // - 或调用接口查询用户是否在指定人群中
    // 示例：return userContext.audienceTags?.includes(audienceId);
    
    return true; // 临时返回 true，待实现
  }, [audienceId]);

  const { displayList, loading, shouldShow } = useCouponRedemption({
    audienceId,
    isInAudience,
    myMemberGoods: myMemberGoods as unknown as MyMemberGoodsResp | null,
    isLoading,
  });

  // 处理标题，最多6个字，优先从 compContent 获取
  const title = useMemo(() => {
    const rawTitle = compContent.title || config?.title || DEFAULT_COMPONENT_TITLE;
    return rawTitle.slice(0, 10);
  }, [compContent.title, config?.title]);

  // 组件容器样式（背景图片 + 上边距）
  const containerStyle = useMemo(() => {
    const style: React.CSSProperties = {};
    // 上边距
    if (spacing) {
      style.paddingTop = `${spacing}px`;
    }
    // 背景图片
    if (bgUrl) {
      style.backgroundImage = `url(${processOssImage(bgUrl)})`;
      style.backgroundSize = '100% auto';
      style.backgroundPosition = 'center top';
      style.backgroundRepeat = 'no-repeat';
    }
    return style;
  }, [bgUrl, spacing]);

  // 组件曝光埋点
  useEffect(() => {
    if (shouldShow) {
      trackGdp('P11222', 'B032', {
        coupon_type: couponType,
        userType,
        versionId: configPageQuery?.data?.versionCode,
        ...getReportCompData(comp),
      });
    }
  }, [shouldShow, couponType, userType, configPageQuery?.data?.versionCode, comp]);

  // 点击埋点上报
  const compClickReport = useCallback(
    (_action: string) => {
      trackGdp('P11222', 'B033', {
        coupon_type: couponType,
        userType,
        versionId: configPageQuery?.data?.versionCode,
        ...getReportCompData(comp),
      });
    },
    [couponType, userType, configPageQuery?.data?.versionCode, comp]
  );

  // 跳转到我的充电卡券页面
  const goToMyCouponPage = useCallback(() => {
    compClickReport('GO_TO_COUPON');
    if (isXPApp) {
      goToPageV2({
        url: 'xiaopeng://charging/card',
        params: {
          type: '1',
        },
      });
    } else if (isWXApp) {
      wxInit().then((wx) =>
        wx.miniProgram.navigateTo({
          url: '/charging/pages/voucher-list/index?tabIndex=1',
        })
      );
    } else if (isMyApp) {
      myInit().then((my) =>
        my.navigateTo({ url: '/charging/pages/voucher-list/index?tabIndex=1' })
      );
    } else {
      showToast('请下载小鹏APP查看充电卡券');
    }
  }, [compClickReport]);

  // 处理卡片点击
  const handleCardClick = useCallback(
    (_item: UnifiedCardItem, _displayItem: DisplayItem) => {
      compClickReport('CARD_CLICK');
      // 卡片点击的跳转逻辑已在 CouponRedemptionList 中处理
      // 这里只做埋点上报，不再调用 goToMyCouponPage 避免重复跳转
    },
    [compClickReport]
  );

  // 处理购买更多点击
  const handleBuyMoreClick = useCallback(() => {
    compClickReport('BUY_MORE');
  }, [compClickReport]);

  // 不展示组件的情况
  if (!shouldShow || loading) {
    return null;
  }

  return (
    <div
      className={`coupon-redemption-component ${bgUrl ? 'with-bg' : ''}`}
      style={containerStyle}
    >
      {/* Header */}
      <div className="redemption-header" onClick={goToMyCouponPage}>
        <div className="header-title">
          <span className="title-text">{title}</span>
        </div>
        <div className="header-right">
          <span>我的充电卡券</span>
          <img
            className="arrow-icon"
            alt=""
            src={processOssImage(IconRightPng)}
          />
        </div>
      </div>

      {/* List */}
      <CouponRedemptionList
        items={displayList}
        onCardClick={handleCardClick}
        onBuyMoreClick={handleBuyMoreClick}
      />
    </div>
  );
};

export default CouponRedemptionComponent;
